import { useCallback, useMemo, useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  IAreaTypeCategoryResponse,
  IBidArea,
  ICategoryFilterResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import {
  filterInactiveAttributes,
  getSelectedAttributeValues,
  sortCategoryAttributes,
} from "@miplatform/utils";
import { useAreaTypes, useCategories, useMaterials } from "@mibuildweb/hooks";
import { useCreateBidContext } from "../CreateBidContext";
import { Floor } from "../CreateBids.types";

export function useConfigureBidViewModel() {
  const [floors, setFloors] = useState<Floor[]>([]);
  const [openAreaDrawer, setOpenAreaDrawer] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [addedFloorIds, setAddedFloorIds] = useState<string[]>([]);
  const [previewAreas, setPreviewAreas] = useState<IBidArea[]>([]);
  const [checkedAreas, setCheckedAreas] = useState<IBidArea[]>([]);

  // Category filter state
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    Partial<ICategoryFilterResponse>
  >({});
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<
    Record<string, string[]>
  >({});
  const [search, setSearch] = useState("");

  // Material results state
  const [materialOptions, setMaterialOptions] = useState<IMaterialsResponse[]>(
    []
  );
  const [filteredMaterialOptions, setFilteredMaterialOptions] = useState<
    IMaterialsResponse[]
  >([]);
  const [openMaterialResults, setOpenMaterialResults] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IMaterialsResponse | null>(
    null
  );
  const [isSuppliedByBuilder, setIsSuppliedByBuilder] = useState(false);
  const [planAreaMaterials, setPlanAreaMaterials] = useState<
    IMaterialsResponse[]
  >([]);

  const debouncedSearch = useDebounce(search, 300);

  const { form } = useCreateBidContext();
  const { watch, setValue } = form;
  const { planAreas = [] } = watch();
  const { areaTypes, getAreaTypes } = useAreaTypes();
  const { getMaterials } = useMaterials();
  const { getCategoryFilters, categoryFilterOptions } = useCategories();

  const getFloors = async () => {
    const response = await homeService.floors.getAll({ sort: "displayOrder" });
    if (response.status === HTTP_STATUSES.OK) {
      setFloors(response.data);
    }
  };

  const planAreaMaterialIds = useMemo(() => {
    const ids = planAreas.flatMap((a) => a.materials?.map((m) => m.materialId));
    return [...new Set(ids)].filter((id) => id);
  }, [planAreas]);

  const getPlanAreaMaterial = async () => {
    const response = await getMaterials({
      filters: { id: { in: { type: "guid", value: planAreaMaterialIds } } },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setPlanAreaMaterials(response.data);
    }
  };

  const getMaterialOptions = async () => {
    const filters = {
      or: [
        { category: { itemCode: { contains: debouncedSearch } } },
        { category: { itemDescription: { contains: debouncedSearch } } },
      ],
      ...(selectedCategory?.categoryId && {
        category: {
          categoryId: { type: "guid", value: selectedCategory.categoryId },
        },
      }),
    };

    const response = await getMaterials({
      sort: { category: "itemCode" },
      filters,
    });

    if (response.status === HTTP_STATUSES.OK) {
      const filteredData = filterInactiveAttributes(response.data).map(
        (material: IMaterialsResponse) => ({
          ...material,
          materialId: material.id,
        })
      );
      setMaterialOptions(filteredData);
      setOpenMaterialResults(true);
    }
  };

  // Grouped areas combining planAreas and previewAreas
  const groupedAreas = useMemo(() => {
    const combinedAreas = [...planAreas, ...previewAreas];
    const sortedAreas = combinedAreas.sort(
      (a, b) => a.displayOrder - b.displayOrder
    );
    if (!sortedAreas.length) return {};
    return Object.groupBy(sortedAreas, (area) => area.floorId);
  }, [planAreas, previewAreas]);

  const addedFloors = useMemo(() => {
    const existingFloorIds = planAreas?.map((a) => a.floorId);
    const allFloorIds = [...new Set([...addedFloorIds, ...existingFloorIds])];
    return allFloorIds
      .map((id) => floors.find((f) => f.id === id))
      .filter((f) => !!f)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [planAreas, floors, addedFloorIds]);

  const hasMissingCategories = useMemo(() => {
    if (!planAreas?.length || !planAreaMaterials.length) return true;
    return planAreas.some((area) => {
      const areaType = areaTypes.find((at) => at.id === area.areaTypeId);
      if (!areaType) return false;
      const requiredCategories = areaType.categories.filter(
        (c) => c.isRequired
      );
      if (!requiredCategories.length) return false;
      const materialIds = area.materials?.map((m) => m.materialId);
      const areaMaterials = planAreaMaterials.filter((m) =>
        materialIds?.includes(m.id)
      );
      return requiredCategories.some(
        (cat) =>
          !areaMaterials.some((m) => m.category?.categoryId === cat.categoryId)
      );
    });
  }, [planAreas, areaTypes, planAreaMaterials]);

  const handleAddFloor = () => {
    const existingFloorIds = planAreas.map((area) => area.floorId);
    const allFloorIds = [...new Set([...addedFloorIds, ...existingFloorIds])];
    const nextFloor = floors.find((f) => !allFloorIds.includes(f.id));
    if (nextFloor) {
      setAddedFloorIds((prev) => [...prev, nextFloor.id]);
    }
  };

  const handleRemoveFloor = (floor: Floor) => {
    const updatedAreas = planAreas.filter((area) => area.floorId !== floor.id);
    setValue("planAreas", updatedAreas, { shouldDirty: true });
    setAddedFloorIds((prev) => prev.filter((id) => id !== floor.id));
    setPreviewAreas((prev) => prev.filter((a) => a.floorId !== floor.id));
  };

  const handleOpenCategoryFilter = (
    area: IBidArea,
    category?: IAreaTypeCategoryResponse
  ) => {
    setCheckedAreas([area]);
    setShowCategoryFilter(true);
    if (category) {
      const sortedCategory = sortCategoryAttributes(category);
      setSelectedCategory(sortedCategory);
    }
  };

  const handleCloseCategoryFilter = () => {
    setShowCategoryFilter(false);
    setCheckedAreas([]);
    resetMaterialDrawerStates();
  };

  const handleCategoryClick = (category: ICategoryFilterResponse) => {
    const sortedCategory = sortCategoryAttributes(category);
    setSelectedCategory(sortedCategory);
  };

  const handleAttributeValueClick = (
    attributeId: string,
    attributeValue: string
  ) => {
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
  };

  const resetMaterialDrawerStates = () => {
    setSelectedCategory({});
    setSelectedAttributeValues({});
    setSearch("");
    setMaterialOptions([]);
    setFilteredMaterialOptions([]);
    setOpenMaterialResults(false);
  };

  const handleCheckArea = (area: IBidArea, checked: boolean) => {
    if (checked) {
      setCheckedAreas((prev) => [...prev, area]);
    } else {
      setCheckedAreas((prev) => prev.filter((a) => a.id !== area.id));
    }
  };

  const handleSelectItem = (material: IMaterialsResponse) => {
    if (selectedItem?.id === material.id) return;
    setIsSuppliedByBuilder(false);
    setSelectedItem(material);
  };

  const handleCancelItem = () => {
    setSelectedItem(null);
    setCheckedAreas([]);
    setIsSuppliedByBuilder(false);
  };

  const handleAddItemToAreas = useCallback(() => {
    if (!selectedItem || !checkedAreas.length) return;

    const updatedAreas = planAreas.map((a) => {
      const isChecked = checkedAreas.some((c) => c.id === a.id);
      if (!isChecked) return a;

      const existingMaterials = a.materials || [];
      const alreadyAdded = existingMaterials.some(
        (m) => m.materialId === (selectedItem.materialId || selectedItem.id)
      );
      if (alreadyAdded) return a;

      return {
        ...a,
        materials: [
          ...existingMaterials,
          {
            materialId: selectedItem.materialId || selectedItem.id,
            suppliedByBuilder: isSuppliedByBuilder,
            quantity: 1,
          },
        ],
      };
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });

    // reset
    setSelectedItem(null);
    setCheckedAreas([]);
    setIsSuppliedByBuilder(false);
    setSelectedCategory({});
    setSelectedAttributeValues({});
  }, [planAreas, selectedItem, checkedAreas, isSuppliedByBuilder]);

  const getAreaType = (area: IBidArea) =>
    areaTypes.find((at) => at.id === area.areaTypeId);

  const isPreviewArea = (area: IBidArea) =>
    previewAreas.some((p) => p.id === area.id);

  const materialResults =
    Object.keys(selectedAttributeValues).length > 0
      ? filteredMaterialOptions
      : materialOptions;

  // prevents refetching on quantity change and same material added
  const planAreaMaterialIdsKey = useMemo(
    () => planAreaMaterialIds.join(","),
    [planAreaMaterialIds]
  );

  return {
    floors,
    openAreaDrawer,
    selectedFloor,
    addedFloorIds,
    addedFloors,
    groupedAreas,
    planAreas,
    showCategoryFilter,
    selectedCategory,
    selectedAttributeValues,
    search,
    materialOptions,
    filteredMaterialOptions,
    materialResults,
    openMaterialResults,
    selectedItem,
    isSuppliedByBuilder,
    checkedAreas,
    planAreaMaterials,
    planAreaMaterialIdsKey,
    hasMissingCategories,
    planAreaMaterialIds,
    debouncedSearch,
    categoryFilterOptions,
    setSearch,
    setOpenAreaDrawer,
    setSelectedFloor,
    setPreviewAreas,
    setOpenMaterialResults,
    setIsSuppliedByBuilder,
    setSelectedItem,
    setFilteredMaterialOptions,
    getAreaType,
    getFloors,
    getAreaTypes,
    getMaterialOptions,
    getPlanAreaMaterial,
    getCategoryFilters,
    isPreviewArea,
    handleAddFloor,
    handleRemoveFloor,
    handleOpenCategoryFilter,
    handleCloseCategoryFilter,
    handleCategoryClick,
    handleAttributeValueClick,
    handleCheckArea,
    handleCancelItem,
    handleSelectItem,
    handleAddItemToAreas,
    resetMaterialDrawerStates,
  };
}
